/**
 * prefillInputs — the `workflow_prefill` inputs shape and its idempotency
 * hash, COPIED (not moved) from
 * `ui/apps/outlook-addin/src/services/orchestratorApi.ts` 1292–1342
 * (`hashPrefillInputs`, `buildPrefillInputs`).
 *
 * COPIED deliberately: `orchestratorApi` keeps its own `triggerPrefill` /
 * `reanalysePrefill`, which call the add-in's private (non-exported)
 * `buildPrefillInputs` + `hashPrefillInputs` directly, until Phase 3 rewires
 * the add-in onto `runPrefill.ts` (which calls THIS copy). Until then, both
 * copies must hash identically — a hashing change in one without the other
 * would mean the add-in and ora stop agreeing on which prefill runs are
 * reusable, silently. There is exactly one behavioural requirement that
 * matters while two copies exist: they must never diverge. Phase 3 deletes
 * the `orchestratorApi.ts` copy.
 *
 * Pure: no React, no Office.js, no network.
 */

/**
 * Deterministic hash of the prefill trigger inputs, used as the substrate
 * idempotency key so identical prefill requests (same email, client, and
 * already-extracted values) reuse the prior completed instance instead of
 * re-running the LLM extraction. Sorted-keys stringify + FNV-1a so key order
 * and object identity never change the hash.
 */
export function hashPrefillInputs(inputs: Record<string, unknown>): string {
  const stableStringify = (value: unknown): string => {
    if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
    const keys = Object.keys(value as Record<string, unknown>).sort();
    const entries = keys.map(
      (k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`,
    );
    return `{${entries.join(",")}}`;
  };
  const text = stableStringify(inputs);
  // FNV-1a, run twice with different offsets for 64 bits of key space.
  const fnv1a = (seed: number): string => {
    let h = seed >>> 0;
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, "0");
  };
  return fnv1a(0x811c9dc5) + fnv1a(0x9747b28c);
}

/**
 * The `workflow_prefill` inputs, built ONCE for both prefill entry points
 * (a normal trigger and a fresh RE-ANALYSE) and for both hosts.
 *
 * Building these in two places is how they would silently stop being the
 * same run: a field added to one and forgotten in the other means RE-ANALYSE
 * (or ora) re-derives from LESS than the add-in's first pass did, and the
 * AM sees fields go blank on a "refresh".
 */
export function buildPrefillInputs(
  targetWorkflowId: string,
  clientSk?: number | null,
  sourceDocumentText?: string,
  alreadyExtracted?: Record<string, unknown>,
  attachmentText?: string,
  internetMessageId?: string | null,
  mailboxAddress?: string | null,
): Record<string, unknown> {
  const inputs: Record<string, unknown> = {
    target_workflow_id: targetWorkflowId,
    client_sk: clientSk ?? null,
    source_document_text: sourceDocumentText ?? "",
    // PDF/attachment text extracted upstream by analyze_email. The substrate
    // folds it into the extraction source so PDF-only identifiers are
    // prefilled — not just email-body fields.
    attachment_text: attachmentText ?? "",
    already_extracted: alreadyExtracted ?? {},
  };
  // Story 26752 / ADO 28067 item 8 (the prefill half — reanalyze_email got
  // the analogous fix in 19f52a99). This is identity ONLY, never a source of
  // extraction itself: it lets workflow_prefill recover source_document_text
  // / attachment_text from the stored email_analysis row when a caller (ora's
  // triage "I sent it" / prepared-brief launch) has no Office mail item to
  // read from and so passes blank text with a real client_sk — as does the
  // add-in for a brief opened from the Today list whose email is not the
  // selected item. Included ONLY when non-empty — a caller with nothing to add
  // keeps producing the exact prior payload shape: unchanged keys, unchanged
  // idempotency hash.
  const trimmedMessageId = (internetMessageId ?? "").trim();
  if (trimmedMessageId) inputs.internet_message_id = trimmedMessageId;
  const trimmedMailbox = (mailboxAddress ?? "").trim();
  if (trimmedMailbox) inputs.mailbox_address = trimmedMailbox;
  return inputs;
}
